// Public trial sign-up — TrakGene-hosted instance only (TGCLOUD-1844, §4.5).
//
// Nothing is created here. The server checks the sign-up code and emails a
// confirmation link; the workspace is created only when that link is used.
// The page shows the same "check your email" whether or not the address
// already has an account, so it cannot be used to find out who uses TrakGene.

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography
} from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import AuthLayout from 'src/components/UI/auth/AuthLayout';
import { injectGoogleCaptchaScript, validateEmail } from 'src/util/util';

const EMPTY = {
  code: '',
  organisation: '',
  firstName: '',
  lastName: '',
  email: '',
  jobTitle: '',
  country: ''
};

export default function TrialSignup() {
  const params = new URLSearchParams(window.location.search);
  const [config, setConfig] = React.useState(null); // null = loading, false = unavailable
  const [form, setForm] = React.useState({
    ...EMPTY,
    code: params.get('code') || ''
  });
  const [accept, setAccept] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [sent, setSent] = React.useState(false);
  const siteKey = React.useRef('');

  React.useEffect(() => {
    axios
      .get(APICONSTANTS.trialConfig)
      .then((res) => setConfig(res.data.available ? res.data : false))
      .catch(() => setConfig(false));
    axios
      .get(APICONSTANTS.recaptcha)
      .then((res) => {
        injectGoogleCaptchaScript(null, res.data.siteKey);
        siteKey.current = res.data.siteKey;
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready =
    form.code.trim() &&
    form.organisation.trim() &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    validateEmail(form.email) &&
    accept;

  const submit = (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const send = (grToken) =>
      axios
        .post(APICONSTANTS.trialSignup, {
          ...form,
          acceptTerms: true,
          termsVersion: config.termsVersion,
          grToken
        })
        .then(() => setSent(true))
        .catch((err) =>
          setError(
            err?.response?.data?.message ||
              'Something went wrong. Please try again.'
          )
        )
        .finally(() => setBusy(false));
    if (!window.grecaptcha || !siteKey.current) {
      setBusy(false);
      setError('The security check did not load. Please refresh the page.');
      return;
    }
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey.current, { action: 'trialSignup' })
        .then(send)
        .catch(() => {
          setBusy(false);
          setError('The security check failed. Please refresh and try again.');
        });
    });
  };

  return (
    <AuthLayout>
      <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Start a TrakGene trial
        </Typography>
        {config === null && <CircularProgress size={24} />}
        {config === false && (
          <Alert severity="info">
            Trial sign-up is not available on this service. Please contact
            TrakGene.
          </Alert>
        )}
        {config && sent && (
          <Alert severity="success">
            Check your email. We have sent a link to <b>{form.email}</b> — open
            it within 24 hours to create your workspace.
          </Alert>
        )}
        {config && !sent && (
          <form onSubmit={submit} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You need the sign-up code TrakGene gave you. Your trial lasts{' '}
              {config.defaultTrialDays || 30} days, and you can invite
              colleagues once it has started.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label="Sign-up code"
                  value={form.code}
                  onChange={set('code')}
                  inputProps={{ autoCapitalize: 'characters' }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label="Organisation"
                  value={form.organisation}
                  onChange={set('organisation')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="First name"
                  value={form.firstName}
                  onChange={set('firstName')}
                  autoComplete="given-name"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Last name"
                  value={form.lastName}
                  onChange={set('lastName')}
                  autoComplete="family-name"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Work email"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Job title"
                  value={form.jobTitle}
                  onChange={set('jobTitle')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={form.country}
                  onChange={set('country')}
                  autoComplete="country-name"
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and accept the{' '}
                      <Link
                        href={config.termsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        trial terms and data processing terms
                      </Link>{' '}
                      (version {config.termsVersion}).
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={!ready || busy}
                  fullWidth
                >
                  {busy ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Send my confirmation link'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </AuthLayout>
  );
}
